import React from "react";
import { Box } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import {
  PersonOutlined,
  NotificationsOutlined,
  PeopleOutlined,
  BusinessOutlined,
  PlaceOutlined,
  PaymentsOutlined,
  BuildOutlined,
  LocalShippingOutlined,
  PaletteOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { SettingsNavSection } from "../hooks/useSettingsNav";
import { dt } from "../../../shared/themes/redesignTokens";

const iconMap: Record<string, React.ReactNode> = {
  PersonOutlined: <PersonOutlined sx={{ fontSize: "18px" }} />,
  NotificationsOutlined: <NotificationsOutlined sx={{ fontSize: "18px" }} />,
  PeopleOutlined: <PeopleOutlined sx={{ fontSize: "18px" }} />,
  BusinessOutlined: <BusinessOutlined sx={{ fontSize: "18px" }} />,
  PlaceOutlined: <PlaceOutlined sx={{ fontSize: "18px" }} />,
  PaymentsOutlined: <PaymentsOutlined sx={{ fontSize: "18px" }} />,
  BuildOutlined: <BuildOutlined sx={{ fontSize: "18px" }} />,
  LocalShippingOutlined: <LocalShippingOutlined sx={{ fontSize: "18px" }} />,
  PaletteOutlined: <PaletteOutlined sx={{ fontSize: "18px" }} />,
};

interface SettingsTabBarProps {
  sections: SettingsNavSection[];
}

const SettingsTabBar: React.FC<SettingsTabBarProps> = ({ sections }) => {
  const location = useLocation();
  const { translate } = useTranslation("settings");

  const isActive = (path: string) =>
    location.pathname === `/settings/${path}` ||
    location.pathname.startsWith(`/settings/${path}/`);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        backgroundColor: dt.cardBg,
        borderBottom: `1px solid ${dt.cardBorder}`,
        borderRadius: "12px 12px 0 0",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {sections.map((section) => {
        const active = isActive(section.path);
        return (
          <NavLink
            key={section.key}
            to={`/settings/${section.path}`}
            style={{ textDecoration: "none" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "12px 16px",
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: active ? 600 : 500,
                color: active ? dt.blue : dt.textSecondary,
                borderBottom: active
                  ? `2px solid ${dt.blue}`
                  : "2px solid transparent",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
                cursor: "pointer",
                "&:hover": {
                  color: active ? dt.blue : dt.textPrimary,
                  backgroundColor: dt.blueSoft,
                },
              }}
            >
              {iconMap[section.iconName]}
              {translate(section.labelKey)}
            </Box>
          </NavLink>
        );
      })}
    </Box>
  );
};

export default SettingsTabBar;
